import { exceptionLogger, log, logException } from '@paradigm/logging';
import { getVersion } from '@paradigm/logging/src/shared';
import { listFeatureFlags } from '@paradigm/features/src/feature-flags/repository';
import { FEATURE_WEB_CACHE_RESET } from '@paradigm/utils/src/constants';

/**
 * Remote recovery hatch (PR4).
 *
 * Lets ops force UI clients to clear their asset caches and hard-reload onto the
 * CURRENT deployed build — either all clients, or only clients running a
 * targeted (declared-bad / stale) version. This is the deliberate override to
 * evict a marooned version on demand; PR3 (SW update resilience) is what keeps
 * the fleet from getting marooned in the first place.
 *
 * Security — the reload is always SAME-ORIGIN onto whatever build is currently
 * live. The directive carries NO url/origin and NO code, so a flipped directive
 * cannot redirect, downgrade, or inject anything; the worst it can do is cause
 * extra reloads (DoS-class), which the loop + rate guards below bound. See
 * .files/plans/2026-06-01-paradigm-cache-reset-remote.yaml (security section).
 */

export type CacheResetMode = 'all' | 'version';

export interface CacheResetDirective {
  readonly mode: CacheResetMode;
  /** version mode: reset clients whose build version is <= this (inclusive). */
  readonly maxStaleVersion?: string;
  /** version mode: explicit versions to reset, in addition to maxStaleVersion. */
  readonly versions?: readonly string[];
  /** Loop guard: act at most once per nonce. Required for mode "all". */
  readonly nonce?: string;
  // NOTE (security): intentionally NO url/origin/code field. Do not add one.
  // The reset is always window.location.reload() onto the current origin.
}

// localStorage keys for the loop + rate guards. These live OUTSIDE anything the
// reset clears (we only clear SW + Cache Storage, never localStorage) so the
// guards survive a reset and prevent a reload loop.
const LAST_NONCE_KEY = 'pdgm.cacheReset.lastNonce';
const LAST_RESET_AT_KEY = 'pdgm.cacheReset.lastResetAt';

// Hard client-side rate cap: never reset more than once per hour, regardless of
// the directive. Bounds a churning-nonce / misconfigured directive to a slow
// drip instead of a reload storm.
const MIN_RESET_INTERVAL_MS = 60 * 60 * 1000;

// Poll cadence (align with the SW update interval ~10 min, plus jitter so the
// fleet doesn't stampede the flag endpoint in lockstep).
const CHECK_INTERVAL_MS = 10 * 60 * 1000;
const CHECK_JITTER_MS = 60 * 1000;

interface GuardState {
  readonly clientVersion: string | undefined;
  readonly directive: CacheResetDirective;
  readonly lastNonce: string | null;
  readonly lastResetAt: number | null;
  readonly now: number;
}

interface ResetDecision {
  readonly reset: boolean;
  readonly reason: string;
}

/**
 * Validate the raw flag config into a directive, reading ONLY known fields.
 * Anything unexpected (e.g. a url field someone tries to smuggle in) is ignored
 * — never read — so it can't influence behaviour. Returns null on anything
 * malformed so the caller fails safe (no reset).
 */
export function parseDirective(value: unknown): CacheResetDirective | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const raw = value as Record<string, unknown>;

  const { mode } = raw;
  if (mode !== 'all' && mode !== 'version') return null;

  const maxStaleVersion =
    typeof raw.maxStaleVersion === 'string' ? raw.maxStaleVersion : undefined;

  const versions =
    Array.isArray(raw.versions) &&
    raw.versions.every((v) => typeof v === 'string')
      ? (raw.versions as string[])
      : undefined;

  const nonce = typeof raw.nonce === 'string' ? raw.nonce : undefined;

  // version mode needs at least one targeting criterion, else it's a no-op
  // (treated as malformed -> fail safe).
  if (mode === 'version' && maxStaleVersion == null && versions == null) {
    return null;
  }

  return { mode, maxStaleVersion, versions, nonce };
}

/** True only for plain dotted-numeric versions like "0.143.1". */
function looksLikeSemver(v: string | undefined): v is string {
  return v != null && /^\d+(\.\d+)*$/u.test(v);
}

/** -1 if a < b, 0 if equal, 1 if a > b. Compares dotted-numeric versions. */
export function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const x = pa[i] ?? 0;
    const y = pb[i] ?? 0;
    if (x < y) return -1;
    if (x > y) return 1;
  }
  return 0;
}

/** Whether this client's build is targeted by the directive. */
export function isVersionTargeted(
  clientVersion: string | undefined,
  directive: CacheResetDirective,
): boolean {
  if (directive.mode === 'all') return true;

  if (clientVersion == null) return false;

  // Explicit list match (exact string — also works for non-semver git-hash builds).
  if (directive.versions?.includes(clientVersion)) return true;

  // Range match: only when BOTH sides look like real semver. Non-prod builds use
  // a git-hash version which is not range-comparable, so they only match the
  // explicit list above.
  if (
    looksLikeSemver(clientVersion) &&
    looksLikeSemver(directive.maxStaleVersion) &&
    compareSemver(clientVersion, directive.maxStaleVersion) <= 0
  ) {
    return true;
  }

  return false;
}

/**
 * Pure decision: given the directive, this client's version, and the persisted
 * guard state, should we reset? Fails safe (no reset) on every uncertainty.
 */
export function decideReset(state: GuardState): ResetDecision {
  const { clientVersion, directive, lastNonce, lastResetAt, now } = state;

  if (!isVersionTargeted(clientVersion, directive)) {
    return { reset: false, reason: 'not targeted' };
  }

  // Rate cap: bounds any directive (even a hostile churning-nonce one) to at
  // most one reset per window, so it can never cause a reload storm.
  if (lastResetAt != null && now - lastResetAt < MIN_RESET_INTERVAL_MS) {
    return { reset: false, reason: 'rate-capped (reset within the last hour)' };
  }

  // mode "all" MUST carry an explicit nonce — otherwise we'd mass-reset every
  // client on every load. Refuse without one.
  if (directive.mode === 'all' && directive.nonce == null) {
    return { reset: false, reason: 'mode=all without nonce' };
  }

  // Loop guard: act at most once per nonce. (version mode is additionally
  // self-limiting — post-reset the client no longer matches the range.)
  if (directive.nonce != null && directive.nonce === lastNonce) {
    return { reset: false, reason: 'nonce already acted on' };
  }

  return { reset: true, reason: `targeted (mode=${directive.mode})` };
}

function safeGetItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (private mode / quota). Without the persisted guard we
    // could loop, so the rate cap is the backstop — but we still prefer to bail.
  }
}

/**
 * The actual reset: drop the service worker + Cache Storage (the stale assets),
 * then hard-reload. The reload is same-origin with no argument, so the browser
 * re-fetches the CURRENT deployed build.
 */
export async function forceCacheReset(): Promise<void> {
  log('Cache reset: clearing service worker + caches and reloading');

  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
  }

  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }

  // NOTE: deliberately NO storage wipe here. localStorage / sessionStorage /
  // IndexedDB are left intact, so this can never log a user out. If a full wipe
  // is ever needed, add it HERE behind a SEPARATE explicit directive flag + its
  // own guard (it is destructive) — see the plan's security.no_storage_wipe.

  // Same-origin, no URL ever sourced from the directive -> current build.
  window.location.reload();
}

/**
 * One check: fetch the directive from the feature-flag channel, decide, and (if
 * targeted + guards pass) record the guard state and reset. Any error or
 * malformed/disabled flag is a no-op.
 */
export async function runCacheResetCheck(): Promise<void> {
  let res;
  try {
    res = await listFeatureFlags({});
  } catch (error) {
    // Network/parse failure -> fail safe, try again next interval.
    logException('Cache reset: failed to fetch directive', error);
    return;
  }
  if (!res.ok) return;

  const flag = res.data[FEATURE_WEB_CACHE_RESET];
  if (flag == null || !flag.isEnabled) return;

  const directive = parseDirective(flag.value);
  if (directive == null) return;

  const decision = decideReset({
    clientVersion: getVersion(),
    directive,
    lastNonce: safeGetItem(LAST_NONCE_KEY),
    lastResetAt: Number(safeGetItem(LAST_RESET_AT_KEY)) || null,
    now: Date.now(),
  });

  if (!decision.reset) {
    log(`Cache reset: skipped (${decision.reason})`);
    return;
  }

  // Persist the guard state BEFORE reloading so the reset cannot loop: the
  // next load sees this nonce/timestamp and the guards short-circuit.
  if (directive.nonce != null) safeSetItem(LAST_NONCE_KEY, directive.nonce);
  safeSetItem(LAST_RESET_AT_KEY, String(Date.now()));

  await forceCacheReset();
}

/**
 * Wire the recovery hatch into app startup: check once on boot, then on an
 * interval so an already-open (possibly stuck) tab is rescued without the user
 * having to reload manually. All checks are best-effort and never throw.
 */
export default function startCacheResetPolling(): void {
  if (NODE_ENV !== 'production') return;

  const check = () =>
    runCacheResetCheck().catch(exceptionLogger('Cache reset check failed'));

  check();

  const jitter = Math.floor(Math.random() * CHECK_JITTER_MS);
  setInterval(check, CHECK_INTERVAL_MS + jitter);
}
